import type { ButtonProps, FlexProps } from '@chakra-ui/react';
import { Button, Flex } from '@chakra-ui/react';
import type { FractionIsh } from 'bi-fraction';
import { Fraction, RoundingMode } from 'bi-fraction';
import type { ReactNode } from 'react';
import React, { useMemo } from 'react';

import { Percent } from '../Num';

type InputChipsProps = Omit<FlexProps, 'onChange'> & {
  value: FractionIsh;
  maxValue: FractionIsh | undefined | null;
  onChange: (value: string) => void;
  decimalPlaces: number;
  renderValue?: (value: string) => ReactNode;
};

export const InputChips = ({
  value,
  maxValue,
  onChange,
  decimalPlaces,
  renderValue,
  ...props
}: InputChipsProps) => {
  const maxValueFraction = useMemo(() => {
    return maxValue ? new Fraction(maxValue) : Fraction.ZERO;
  }, [maxValue]);

  const percentageValueList = useMemo((): {
    value: string;
    percent: Fraction;
  }[] => {
    if (!maxValue) {
      return [];
    }
    return [...Array(4)].map((_, i) => {
      const percent = new Fraction(i + 1, 4);
      const valueFraction = percent.eq(1)
        ? maxValueFraction
        : maxValueFraction.mul(percent);

      return {
        percent,
        value: valueFraction.toFixed(decimalPlaces, {
          decimalPlaces,
          roundingMode: RoundingMode.ROUND_DOWN,
          trailingZeros: false,
        }),
      };
    });
  }, [decimalPlaces, maxValue, maxValueFraction]);

  const isDisabled = !maxValue || maxValueFraction.lte(0);

  return (
    <Flex gap="8px" {...props}>
      {percentageValueList.map((item, i) => {
        const isActive = item.value === value;

        return (
          <InputChip
            key={i}
            isActive={isActive}
            disabled={isDisabled}
            onClick={() => {
              onChange(item.value);
            }}
          >
            {renderValue?.(item.value)}
            {!renderValue &&
              (item.percent.eq(1) ? 'MAX' : <Percent value={item.percent} />)}
          </InputChip>
        );
      })}
    </Flex>
  );
};

type InputChipProps = ButtonProps & {
  isActive: boolean;
};
const InputChip = ({ isActive, ...props }: InputChipProps) => {
  const dataActiveProp = isActive
    ? {
        'data-active': isActive,
      }
    : {};
  return (
    <Button
      variant="chip"
      size="sm"
      flex="1"
      justifyContent="center"
      rounded="8px"
      color={isActive ? 'background.offWhite' : undefined}
      {...dataActiveProp}
      {...props}
    />
  );
};
